import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import type { CostItem } from './CreditCostNotice';

// E-commerce cobra pelo custo de provedor, etapa por etapa (imagem, clipe, segundos de avatar,
// tokens de LLM…), quando cada etapa termina. O valor da tela é a ESTIMATIVA que o backend
// calcula com a tabela de custos e o modelo de IA da área (POST /ecommerce-*/estimate).

export interface PipelineStep {
  kind?: string;
  [k: string]: any;
}

export type EcomKind = 'photo' | 'video';

export const ESTIMATE_NOTE = 'Estimativa: cada etapa é cobrada pelo uso real quando termina; etapa que falha não cobra.';

// Cache em memória por forma do pipeline (tipo + kinds/ids das etapas).
const cache = new Map<string, Promise<number | null>>();

function stepsKey(kind: EcomKind, steps: PipelineStep[]): string {
  return `${kind}|${(steps || []).map(s => `${s?.kind || ''}:${s?.id || ''}${s?.avatarEngine ? `:${s.avatarEngine}` : ''}`).join(',')}`;
}

export function fetchPipelineEstimate(kind: EcomKind, steps: PipelineStep[]): Promise<number | null> {
  const key = stepsKey(kind, steps);
  if (!cache.has(key)) {
    // avatarEngine muda a tarifa por segundo da etapa de avatar (Avatar III x IV).
    const slim = (steps || []).map(s => ({ id: s?.id, kind: s?.kind, title: s?.title, ...(s?.avatarEngine ? { avatarEngine: s.avatarEngine } : {}) }));
    const p = api.estimateEcommercePipeline(kind, { steps: slim })
      .then(r => (r?.success ? Number(r.estimate?.totalCredits) || 0 : null))
      .catch(() => { cache.delete(key); return null; });
    cache.set(key, p);
  }
  return cache.get(key)!;
}

// Total estimado (créditos) de um pipeline. null enquanto carrega ou se falhar.
export function usePipelineEstimate(kind: EcomKind, steps: PipelineStep[]): number | null {
  const key = stepsKey(kind, steps);
  const [total, setTotal] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    setTotal(null);
    if (!(steps || []).length) { setTotal(0); return; }
    fetchPipelineEstimate(kind, steps).then(v => { if (alive) setTotal(v); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return total;
}

// Items prontos para CreditCostNotice / ConfirmCreditsModal / PipelineCostBadge.
export function estimateItems(total: number | null, label = 'execução (estimativa)'): CostItem[] {
  if (total == null || total <= 0) return [];
  return [{ featureKey: 'ecom_estimate', qty: 1, label, unitCredits: total }];
}

export function usePipelineCostItems(kind: EcomKind, steps: PipelineStep[]): CostItem[] {
  return estimateItems(usePipelineEstimate(kind, steps));
}

// Estimativa de UMA etapa (regerar/reprocessar).
export function useStepCostItems(kind: EcomKind, step: PipelineStep | null | undefined): CostItem[] {
  const total = usePipelineEstimate(kind, step ? [step] : []);
  return step ? estimateItems(total, 'geração (estimativa)') : [];
}

// Total em créditos a partir dos items (usa unitCredits quando vier pronto do backend).
export function itemsTotal(items: CostItem[], getCost: (featureKey: string, qty?: number) => number): number {
  return items.reduce((sum, it) => sum + (it.unitCredits != null ? it.unitCredits * it.qty : getCost(it.featureKey, it.qty)), 0);
}
